import { useState, useEffect, useCallback } from 'react';
import {
  AUTO_PATH_LAYER,
  CUSTOM_PATH_LAYER,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  DISTANCE_LABELS_LAYER,
  CITY_ZOOM_THRESHOLD
} from '../constants';
import { useAutoHideDistanceLabels } from './useAutoHideDistanceLabels';

/**
 * Hook for managing map layer visibility
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @returns {Object} Layer control state and functions
 */
export const useLayerControl = (map, mapReady) => {
  // Layer visibility state
  const [layerVisibility, setLayerVisibility] = useState({
    autoPath: true,
    customPath: true,
    locationPoints: true, // Layer 3 + Layer 4 combined control
    distanceLabels: true
  });

  // Auto-hide distance labels state
  const [autoHideInfo, setAutoHideInfo] = useState({
    isAutoHidden: false,
    shouldAutoHide: false,
    currentZoom: 12
  });

  // Handle auto-visibility change callback
  const handleAutoVisibilityChange = useCallback((info) => {
    setAutoHideInfo({
      isAutoHidden: info.isAutoHidden,
      shouldAutoHide: info.shouldAutoHide,
      currentZoom: info.zoom
    });
  }, []);

  // Handle distance labels auto-enable callback
  const handleDistanceLabelsAutoEnable = useCallback((shouldEnable) => {
    if (shouldEnable) {
      setLayerVisibility(prev => ({
        ...prev,
        distanceLabels: true
      }));
    }
  }, []);

  // Use auto-hide distance labels hook
  const { 
    isAutoHidden, 
    cityZoomThreshold, 
    shouldAutoHide,
    forceUpdateVisibility 
  } = useAutoHideDistanceLabels(
    map, 
    mapReady, 
    layerVisibility.distanceLabels,
    handleAutoVisibilityChange,
    handleDistanceLabelsAutoEnable
  );

  // Set individual layer visibility
  const setLayerVisibilityInternal = useCallback((layerKey, visible) => {
    if (!map || !mapReady) return false;

    try {
      const visibility = visible ? 'visible' : 'none';
      
      switch (layerKey) {
        case 'autoPath':
          if (map.getLayer(AUTO_PATH_LAYER)) {
            map.setLayoutProperty(AUTO_PATH_LAYER, 'visibility', visibility);
          }
          break;
          
        case 'customPath':
          if (map.getLayer(CUSTOM_PATH_LAYER)) {
            map.setLayoutProperty(CUSTOM_PATH_LAYER, 'visibility', visibility);
          }
          break;
          
        case 'locationPoints':
          // Control Layer 3 and Layer 4 together
          if (map.getLayer(POINTS_CIRCLE_LAYER)) {
            map.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', visibility);
          }
          if (map.getLayer(POINTS_SYMBOL_LAYER)) {
            map.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', visibility);
          }
          break;
          
        case 'distanceLabels':
          // Distance labels need special handling due to auto-hide logic
          if (map.getLayer(DISTANCE_LABELS_LAYER)) {
            const currentZoom = map.getZoom();
            const shouldAutoHide = currentZoom <= CITY_ZOOM_THRESHOLD;
            
            if (shouldAutoHide) {
              // At city level, unconditionally hide, don't allow user control
              map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', 'none');
              return false; // Return false to indicate operation blocked
            } else {
              // At non-city level, allow user control
              map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', visibility);
              // Immediately trigger re-evaluation of auto-hide logic
              setTimeout(() => {
                if (forceUpdateVisibility) {
                  forceUpdateVisibility();
                }
              }, 10);
            }
          }
          break;
          
        default:
          return false;
      }
      
      return true;
    } catch (error) {
      console.error(`Error setting layer visibility for ${layerKey}:`, error);
      return false;
    }
  }, [map, mapReady, forceUpdateVisibility]);

  // Toggle layer visibility state
  const toggleLayer = useCallback((layerKey) => {
    // Special handling for distance labels at city level
    if (layerKey === 'distanceLabels' && map && mapReady) {
      const currentZoom = map.getZoom();
      const shouldAutoHide = currentZoom <= CITY_ZOOM_THRESHOLD;
      
      if (shouldAutoHide && !layerVisibility[layerKey]) {
        // Trying to enable distance labels at city level, show alert and block operation
        alert('Distance labels are auto-hidden at city level. Please zoom in to enable.');
        return false;
      }
    }

    const newVisibility = !layerVisibility[layerKey];
    const success = setLayerVisibilityInternal(layerKey, newVisibility);
    
    if (success) {
      setLayerVisibility(prev => ({
        ...prev,
        [layerKey]: newVisibility
      }));
    }
    
    return success;
  }, [layerVisibility, setLayerVisibilityInternal, map, mapReady]);

  // Show all layers
  const showAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      if (!layerVisibility[key]) {
        const success = setLayerVisibilityInternal(key, true);
        if (success) {
          updates[key] = true;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal]);

  // Hide all layers
  const hideAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      if (layerVisibility[key]) {
        const success = setLayerVisibilityInternal(key, false);
        if (success) {
          updates[key] = false;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal]);

  // Sync current layer state from map
  const syncLayerVisibility = useCallback(() => {
    if (!map || !mapReady) return;

    try {
      const updates = {};
      
      // Check auto path layer
      if (map.getLayer(AUTO_PATH_LAYER)) {
        updates.autoPath = map.getLayoutProperty(AUTO_PATH_LAYER, 'visibility') !== 'none';
      }
      
      // Check custom path layer
      if (map.getLayer(CUSTOM_PATH_LAYER)) {
        updates.customPath = map.getLayoutProperty(CUSTOM_PATH_LAYER, 'visibility') !== 'none';
      }
      
      // Check location points layer (based on circle layer)
      if (map.getLayer(POINTS_CIRCLE_LAYER)) {
        updates.locationPoints = map.getLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility') !== 'none';
      }
      
      // Check distance labels layer
      if (map.getLayer(DISTANCE_LABELS_LAYER)) {
        updates.distanceLabels = map.getLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility') !== 'none';
      }
      
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error syncing layer visibility:', error);
    }
  }, [map, mapReady]);

  // Listen to map state changes and auto-sync layer visibility
  useEffect(() => {
    if (map && mapReady) {
      // Initial sync
      syncLayerVisibility();
      
      // Listen to layer style changes (optional)
      const handleStyleData = () => {
        // Delayed sync to ensure style updates are complete
        setTimeout(syncLayerVisibility, 100);
      };
      
      map.on('styledata', handleStyleData);
      
      return () => {
        if (map.off) {
          map.off('styledata', handleStyleData);
        }
      };
    }
  }, [map, mapReady, syncLayerVisibility]);

  return {
    layerVisibility,
    toggleLayer,
    showAllLayers,
    hideAllLayers,
    syncLayerVisibility,
    setLayerVisibility: (key, visible) => {
      const success = setLayerVisibilityInternal(key, visible);
      if (success) {
        setLayerVisibility(prev => ({
          ...prev,
          [key]: visible
        }));
      }
      return success;
    },
    // Distance labels auto-hide related info
    distanceLabelsAutoHide: {
      isAutoHidden,
      shouldAutoHide,
      cityZoomThreshold,
      autoHideInfo
    }
  };
};
